import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { GroupAnalysis, LinkedTransfer, MapData, TerminalTrade } from '@pleco/shared';
import type { MapPulse } from '../components/BubbleCanvas.js';
import { fmtUsd, shortAddr } from './fmt.js';
import { terminalApi } from './terminalApi.js';

/**
 * Live layer of a token map.
 *
 * Trades arrive from the poller in batches every ~5 s, but each one has
 * its on-chain time. Instead of applying a batch at once (every bubble
 * jumping together every 5 s), trades are replayed at their real pace:
 * a batch is spread over the next few seconds in the order and spacing it
 * happened, and a 1 s clock applies whatever is due. Each tick updates the
 * holdings (snapshot + every trade after it), flashes the wallets that
 * traded, and checks for alerts (whales, linked groups dumping, the dev
 * selling, linked wallets sending tokens to exchanges).
 */

export interface LiveAlert {
  id: string;
  at: number;
  ts: number;
  tone: 'danger' | 'warn' | 'good' | 'info';
  title: string;
  detail: string;
  wallet?: string;
  groupId?: string;
}

const TICK_MS = 1000;
/** A poll batch is replayed over at most this long. */
const SPREAD_S = 4;
const GROUP_WINDOW_S = 120;
const MAX_ALERTS = 30;

interface Queued {
  t: TerminalTrade;
  at: number;
}
const keyOf = (t: TerminalTrade) => `${t.signature}:${t.idx}`;

export function useLiveFeed({
  data,
  trades,
  analysis,
  solUsd,
  groupName,
}: {
  data: MapData;
  trades: TerminalTrade[];
  analysis?: GroupAnalysis;
  solUsd?: number;
  groupName: (id: string) => string | undefined;
}) {
  const mint = data.token.address;
  const supply = data.token.trackedSupply || data.token.totalSupply || 1;
  const mcap = data.token.marketCapUsd ?? (data.token.priceUsd ? data.token.priceUsd * data.token.totalSupply : undefined);
  /** A single trade worth an alert: big for this token, at least $5K. */
  const whaleUsd = Math.min(250_000, Math.max(5_000, (mcap ?? 0) * 0.00002));

  const base = useMemo(() => new Map(data.nodes.map((n) => [n.address, n.percent])), [data.nodes]);
  const seen = useRef(new Set<string>());
  const played = useRef(new Map<string, TerminalTrade>());
  const queue = useRef<Queued[]>([]);
  const lastAt = useRef(0);
  const [live, setLive] = useState<Map<string, number>>();
  const [pulses, setPulses] = useState<MapPulse[]>([]);
  const [alerts, setAlerts] = useState<LiveAlert[]>([]);
  const ctx = useRef({ analysis, solUsd, whaleUsd, supply, groupName });
  ctx.current = { analysis, solUsd, whaleUsd, supply, groupName };
  const groupFlow = useRef(new Map<string, { buy: number; sell: number; trades: { ts: number; side: 'buy' | 'sell'; usd: number; pct: number }[] }>());

  // new token: start over
  useEffect(() => {
    seen.current = new Set();
    played.current = new Map();
    queue.current = [];
    lastAt.current = 0;
    groupFlow.current = new Map();
    setLive(undefined);
    setPulses([]);
    setAlerts([]);
  }, [mint]);

  const push = useCallback((a: LiveAlert) => {
    setAlerts((cur) => [a, ...cur.filter((x) => x.id !== a.id)].slice(0, MAX_ALERTS));
  }, []);

  /** Holdings now = snapshot + every played trade after the snapshot. */
  const recompute = useCallback(() => {
    const delta = new Map<string, number>();
    for (const t of played.current.values()) {
      if (t.ts <= data.asOf) continue;
      const d = ((t.side === 'buy' ? 1 : -1) * t.tokenAmount * 100) / supply;
      delta.set(t.trader, (delta.get(t.trader) ?? 0) + d);
    }
    const next = new Map<string, number>();
    for (const [a, d] of delta) {
      const b = base.get(a) ?? 0;
      let v = b + d;
      // sold (nearly) everything: dust left from rounding counts as gone
      if (v <= Math.max(1e-7, b * 0.002)) v = 0;
      next.set(a, v);
    }
    setLive((cur) => {
      if (cur && cur.size === next.size && [...next].every(([k, v]) => Math.abs((cur.get(k) ?? -1) - v) < 1e-9)) return cur;
      return next;
    });
  }, [base, data.asOf, supply]);

  // a new snapshot arrived: re-base on it
  useEffect(() => recompute(), [recompute]);

  // queue trades as they arrive, spread over the next seconds by their real timing
  useEffect(() => {
    const fresh = trades.filter((t) => !seen.current.has(keyOf(t)));
    if (!fresh.length) return;
    const first = seen.current.size === 0;
    for (const t of fresh) seen.current.add(keyOf(t));
    const nowS = Date.now() / 1000;
    if (first) {
      // history already on screen when the page opened: apply at once, no flashes
      for (const t of fresh) played.current.set(keyOf(t), t);
      recompute();
      return;
    }
    const old = fresh.filter((t) => nowS - t.ts > 90);
    for (const t of old) played.current.set(keyOf(t), t);
    const recent = fresh.filter((t) => nowS - t.ts <= 90).sort((a, b) => a.ts - b.ts || a.slot - b.slot);
    if (recent.length) {
      const a = recent[0]!.ts;
      const span = recent[recent.length - 1]!.ts - a;
      const scale = span > SPREAD_S ? SPREAD_S / span : 1;
      const start = Math.max(performance.now(), lastAt.current);
      for (const t of recent) {
        const at = start + (t.ts - a) * scale * 1000;
        queue.current.push({ t, at });
        lastAt.current = Math.max(lastAt.current, at);
      }
    }
    if (old.length) recompute();
  }, [trades, recompute]);

  // the 1 s clock
  useEffect(() => {
    const id = setInterval(() => {
      const now = performance.now();
      const due = queue.current.filter((q) => q.at <= now);
      if (!due.length) return;
      queue.current = queue.current.filter((q) => q.at > now);
      const p: MapPulse[] = [];
      for (const { t } of due) {
        played.current.set(keyOf(t), t);
        p.push({ address: t.trader, side: t.side, at: now });
      }
      setPulses((cur) => [...p, ...cur.filter((x) => now - x.at < 3000)].slice(0, 60));
      recompute();
      for (const { t } of due) checkTrade(t);
    }, TICK_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recompute]);

  function checkTrade(t: TerminalTrade) {
    const c = ctx.current;
    const usd = t.valueUsd ?? (c.solUsd ? t.solAmount * c.solUsd : 0);
    const pct = (t.tokenAmount * 100) / c.supply;
    const info = c.analysis?.wallets[t.trader];
    const isDev = info?.tags.includes('dev') || c.analysis?.launch?.dev === t.trader;
    const verb = t.side === 'buy' ? 'bought' : 'sold';
    if (isDev && t.side === 'sell') {
      push({ id: `dev:${t.signature}`, at: Date.now(), ts: t.ts, tone: 'danger', title: `The creator sold ${fmtUsd(usd)}`, detail: `${fmtPct(pct)} of supply · ${shortAddr(t.trader)}`, wallet: t.trader });
    } else if (usd >= c.whaleUsd || pct >= 0.3) {
      const gname = info?.groupId ? c.groupName(info.groupId) : undefined;
      push({
        id: `whale:${t.signature}:${t.idx}`,
        at: Date.now(),
        ts: t.ts,
        tone: t.side === 'buy' ? 'good' : 'warn',
        title: `${gname ? `${gname} wallet` : 'Whale'} ${verb} ${fmtUsd(usd)}`,
        detail: `${fmtPct(pct)} of supply · ${shortAddr(t.trader)}`,
        wallet: t.trader,
        groupId: info?.groupId,
      });
    }
    // linked group moving together over the last 2 minutes
    const gid = info?.groupId;
    if (!gid) return;
    const f = groupFlow.current.get(gid) ?? { buy: 0, sell: 0, trades: [] };
    f.trades.push({ ts: t.ts, side: t.side, usd, pct });
    f.trades = f.trades.filter((x) => t.ts - x.ts <= GROUP_WINDOW_S);
    f.buy = f.trades.filter((x) => x.side === 'buy').reduce((s, x) => s + x.usd, 0);
    f.sell = f.trades.filter((x) => x.side === 'sell').reduce((s, x) => s + x.usd, 0);
    const sellPct = f.trades.filter((x) => x.side === 'sell').reduce((s, x) => s + x.pct, 0);
    const buyPct = f.trades.filter((x) => x.side === 'buy').reduce((s, x) => s + x.pct, 0);
    groupFlow.current.set(gid, f);
    const name = c.groupName(gid) ?? 'Linked group';
    const big = (v: number, p: number) => v >= c.whaleUsd / 2 || p >= 0.25;
    const windowId = Math.floor(t.ts / GROUP_WINDOW_S);
    if (big(f.sell, sellPct) && f.sell >= f.buy) {
      // one alert per group per window; it updates as the dump grows
      push({ id: `gsell:${gid}:${windowId}`, at: Date.now(), ts: t.ts, tone: 'danger', title: `${name} sold ${fmtUsd(f.sell)}`, detail: `${fmtPct(sellPct)} of supply in the last 2 min`, groupId: gid });
    } else if (big(f.buy, buyPct) && f.buy > f.sell) {
      push({ id: `gbuy:${gid}:${windowId}`, at: Date.now(), ts: t.ts, tone: 'warn', title: `${name} bought ${fmtUsd(f.buy)}`, detail: `${fmtPct(buyPct)} of supply in the last 2 min`, groupId: gid });
    }
  }

  // linked wallets moving tokens out by transfer (server watches them)
  const [transfers, setTransfers] = useState<LinkedTransfer[]>([]);
  useEffect(() => {
    let alive = true;
    let since = 0;
    const seenT = new Set<string>();
    const poll = async () => {
      try {
        const r = await terminalApi.flows(mint, since);
        if (!alive) return;
        const fresh = r.transfers.filter((x) => !seenT.has(`${x.signature}:${x.from}:${x.to}`));
        for (const x of fresh) {
          seenT.add(`${x.signature}:${x.from}:${x.to}`);
          since = Math.max(since, x.ts - 1);
          const c = ctx.current;
          const gname = x.groupId ? c.groupName(x.groupId) : undefined;
          const who = gname ? `${gname} wallet` : 'Linked wallet';
          if (x.toKind === 'cex')
            push({ id: `cex:${x.signature}:${x.from}`, at: Date.now(), ts: x.ts, tone: 'danger', title: `${who} sent ${fmtPct(x.percent)} to ${x.toLabel ?? 'an exchange'}`, detail: `Exchange deposits usually come before a sell · ${shortAddr(x.from)}`, wallet: x.from, groupId: x.groupId });
          else if (x.toKind === 'wallet' && x.percent >= 0.05)
            push({ id: `split:${x.signature}:${x.from}`, at: Date.now(), ts: x.ts, tone: 'warn', title: `${who} moved ${fmtPct(x.percent)} to a new wallet`, detail: `${shortAddr(x.from)} → ${shortAddr(x.to)} · splitting a bag hides it from the map`, wallet: x.to, groupId: x.groupId });
        }
        if (fresh.length) setTransfers((cur) => [...fresh, ...cur].slice(0, 40));
      } catch {
        /* next round */
      }
    };
    void poll();
    const id = setInterval(poll, 15_000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [mint, push]);

  const dismiss = useCallback((id: string) => setAlerts((cur) => cur.filter((a) => a.id !== id)), []);
  return { live, pulses, alerts, dismiss, transfers };
}

const fmtPct = (p: number) => (p >= 10 ? `${p.toFixed(0)}%` : p >= 0.1 ? `${p.toFixed(1)}%` : p >= 0.01 ? `${p.toFixed(2)}%` : '<0.01%');
